// Лаборатория (Этап 3, раздел 6.10 ТЗ): входной контроль сырья, рецептуры с версиями, испытания образцов, паспорта качества.

import { useState } from "react";
import { fmtDate, fmtDateTime, fmtNum, fmtTime, localISODate, toDate, zonedISO } from "../../shared/format";
import { useLive } from "../../shared/live";
import { TopBar } from "../../shared/shell";
import { Button, Field, Input, Kpi, Loading, Modal, ReasonDialog, Select, Tabs, useAsync, useToast } from "../../shared/ui";
import { ControlDialog, LAB, qty, ReceiptsTable } from "../components/StockParts";
import { api, live, useCtx } from "../ctx";

type Tab = "control" | "recipes" | "tests" | "passports";

const RECIPE: Record<string, { label: string; cls: string }> = {
  draft: { label: "черновик", cls: "s-transit" },
  approved: { label: "утверждена", cls: "s-done" },
  archived: { label: "в архиве", cls: "" },
};
const RESULT: Record<string, { label: string; cls: string }> = {
  pending: { label: "ждёт испытания", cls: "s-transit" },
  passed: { label: "соответствует", cls: "s-done" },
  failed: { label: "не соответствует", cls: "s-conflict" },
};

export function LabPage() {
  const { me } = useCtx();
  const edit = ["lab", "admin"].includes(me.role);
  const [tab, setTab] = useState<Tab>("control");
  const pending = useAsync<any[]>(() => api.get("/stock/receipts", { lab_status: "pending" }), []);
  const tests = useAsync<any[]>(() => api.get("/lab/tests"), []);
  const recipes = useAsync<any>(() => api.get("/lab/recipes"), []);
  useLive(live, ["stock."], () => { pending.reload(); tests.reload(); recipes.reload(); }, 800);
  const overdue = (tests.data || []).filter((t) => t.overdue).length;
  const failed = (tests.data || []).filter((t) => t.result === "failed").length;
  const without = recipes.data?.grades_without_recipe?.length || 0;
  return (
    <>
      <TopBar title="Лаборатория" />
      <div className="content">
        <div className="kpis">
          <Kpi k="Партии ждут контроля" v={pending.data?.length ?? "…"} tone={pending.data?.length ? "warn" : undefined} />
          <Kpi k="Испытания: пора испытать" v={overdue} tone={overdue ? "warn" : undefined} s="срок испытания уже наступил" />
          <Kpi k="Не соответствует классу" v={failed} tone={failed ? "bad" : undefined} />
          <Kpi k="Марки без рецептуры" v={without} tone={without ? "warn" : undefined} s={recipes.data?.grades_without_recipe?.slice(0, 4).map((g: any) => g.code).join(", ")} />
        </div>
        <Tabs value={tab} onChange={setTab} items={[
          { value: "control", label: `Входной контроль${pending.data?.length ? ` (${pending.data.length})` : ""}` },
          { value: "recipes", label: "Рецептуры" }, { value: "tests", label: "Испытания" }, { value: "passports", label: "Паспорта качества" }]} />
        {tab === "control" && <Control edit={edit} onChanged={pending.reload} />}
        {tab === "recipes" && (!recipes.data ? <Loading error={recipes.error} onRetry={recipes.reload} /> : <Recipes data={recipes.data} edit={edit} reload={recipes.reload} />)}
        {tab === "tests" && (!tests.data ? <Loading error={tests.error} onRetry={tests.reload} /> : <Tests rows={tests.data} edit={edit} reload={tests.reload} />)}
        {tab === "passports" && <Passports />}
      </div>
    </>
  );
}

// ---------------------------------------------------------------- входной контроль

function Control({ edit, onChanged }: { edit: boolean; onChanged: () => void }) {
  const [status, setStatus] = useState("pending");
  const r = useAsync<any[]>(() => api.get("/stock/receipts", { lab_status: status || undefined, start: status === "pending" ? undefined : addDaysIso(-30) }), [status]);
  const [sel, setSel] = useState<any>(null);
  useLive(live, ["stock."], () => r.reload(), 800);
  const rows = (r.data || []).filter((x) => status || x.lab_status !== "not_required");
  return (
    <div className="stack">
      <div className="row">
        <Field label="Показать"><Select value={status} onChange={(e) => setStatus(e.target.value)}
          options={[{ value: "pending", label: "Ждут проверки" }, { value: "accepted", label: "Годные (30 дней)" }, { value: "rejected", label: "Не годные (30 дней)" }, { value: "", label: "Все с контролем (30 дней)" }]} /></Field>
      </div>
      {!r.data ? <Loading error={r.error} onRetry={r.reload} /> : status === "pending" && !rows.length
        ? <p className="empty">Все партии проверены.</p>
        : <ReceiptsTable rows={rows} onControl={edit ? setSel : undefined} />}
      {status !== "pending" && rows.some((x) => x.lab_results.length) && (
        <details className="panel pad"><summary>Показатели проверенных партий</summary>
          <table className="table">
            <thead><tr><th>Партия</th><th>Показатель</th><th>Значение</th><th>Норма</th></tr></thead>
            <tbody>{rows.flatMap((x) => x.lab_results.map((l: any, i: number) => (
              <tr key={x.id + i}><td>{i === 0 ? `${x.material}, ${fmtDate(x.at)} (${LAB[x.lab_status]?.label})` : ""}</td><td>{l.name}</td><td>{l.value}</td><td>{l.norm || ""}</td></tr>
            )))}</tbody>
          </table>
        </details>
      )}
      {sel && <ControlDialog receipt={sel} onClose={() => setSel(null)} onDone={() => { setSel(null); r.reload(); onChanged(); }} />}
    </div>
  );
}

function addDaysIso(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return localISODate(d);
}

// ---------------------------------------------------------------- рецептуры

function Recipes({ data, edit, reload }: { data: any; edit: boolean; reload: () => void }) {
  const { refs } = useCtx();
  const toast = useToast();
  const [grade, setGrade] = useState("");
  const [showArchive, setShowArchive] = useState(false);
  const [editor, setEditor] = useState<null | { recipe?: any; base?: any; grade_id?: string }>(null);
  const [archive, setArchive] = useState<any>(null);
  const rows: any[] = data.rows.filter((r: any) => (!grade || r.grade_id === grade) && (showArchive || r.status !== "archived"));
  const byGrade = new Map<string, any[]>();
  for (const r of rows) byGrade.set(r.grade, [...(byGrade.get(r.grade) || []), r]);
  const act = async (fn: () => Promise<any>, ok: string) => { try { await fn(); toast.ok(ok); reload(); } catch (e) { toast.error(e); } };
  return (
    <div className="stack">
      <div className="row" style={{ alignItems: "flex-end" }}>
        <Field label="Марка"><Select value={grade} onChange={(e) => setGrade(e.target.value)} placeholder="Все марки" options={refs.grades.map((g) => ({ value: g.id, label: g.code }))} /></Field>
        <label className="check small"><input type="checkbox" checked={showArchive} onChange={(e) => setShowArchive(e.target.checked)} />показать архив</label>
        {edit && <Button variant="primary" onClick={() => setEditor({ grade_id: grade || undefined })}>Новая рецептура</Button>}
      </div>
      {data.grades_without_recipe.length > 0 && (
        <div className="panel pad small">
          <b>Нет утверждённой рецептуры:</b> {data.grades_without_recipe.map((g: any) => g.code).join(", ")}. Замесы этих марок не списывают сырьё со склада.
        </div>
      )}
      {[...byGrade.entries()].map(([code, list]) => (
        <div key={code} className="panel">
          <div className="ph"><b>{code}</b></div>
          <div className="pb stack tight">
            {list.map((r) => (
              <div key={r.id} className="row" style={{ justifyContent: "space-between", alignItems: "flex-start", flexWrap: "nowrap", gap: 16, paddingBottom: 8, borderBottom: "1px solid var(--line-soft)" }}>
                <div className="stack tight" style={{ minWidth: 0 }}>
                  <div>
                    <b>Версия {r.version}</b> · {r.plant_name} · <span className={`tag ${RECIPE[r.status]?.cls}`}>{RECIPE[r.status]?.label}</span>
                    {r.active_for.length > 0 && <span className="tag s-done" style={{ marginLeft: 6 }}>действует: {r.active_for.join(", ")}</span>}
                  </div>
                  <div className="small muted">с {fmtDateTime(r.valid_from)}{r.approved_by ? ` · утвердил ${r.approved_by} ${fmtDate(r.approved_at)}` : ""}{r.comment ? ` · ${r.comment}` : ""}</div>
                  <div className="small">{r.items.map((i: any) => `${i.material} — ${fmtNum(i.qty, i.qty >= 100 ? 0 : 1)} ${i.unit}`).join("; ")}<span className="muted"> (на 1 м³)</span></div>
                </div>
                {edit && (
                  <div className="row tight" style={{ flexWrap: "nowrap" }}>
                    {r.status === "draft" && <Button size="small" onClick={() => setEditor({ recipe: r })}>Изменить</Button>}
                    {r.status === "draft" && <Button size="small" variant="primary" onClick={() => act(() => api.post(`/lab/recipes/${r.id}/approve`), `Версия ${r.version} утверждена`)}>Утвердить</Button>}
                    {r.status === "approved" && <Button size="small" onClick={() => setEditor({ base: r })}>Новая версия</Button>}
                    {r.status !== "archived" && <Button size="small" variant="ghost" onClick={() => setArchive(r)}>В архив</Button>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
      {!rows.length && <p className="empty">Рецептур нет.</p>}
      {editor && <RecipeEditor {...editor} onClose={() => setEditor(null)} onDone={() => { setEditor(null); reload(); }} />}
      {archive && (
        <ReasonDialog title={`Убрать в архив: ${archive.grade}, версия ${archive.version}?`} action="В архив" danger onCancel={() => setArchive(null)}
          onConfirm={(reason) => act(() => api.post(`/lab/recipes/${archive.id}/archive`, { reason }), "Рецептура в архиве").then(() => setArchive(null))} />
      )}
    </div>
  );
}

function RecipeEditor({ recipe, base, grade_id, onClose, onDone }: { recipe?: any; base?: any; grade_id?: string; onClose: () => void; onDone: () => void }) {
  const { refs } = useCtx();
  const toast = useToast();
  const mats = useAsync<any>(() => api.get("/materials", { limit: 500 }), []);
  const src = recipe || base;
  const now = new Date();
  const from = recipe ? toDate(recipe.valid_from)! : now;
  const [v, setV] = useState({
    grade_id: src?.grade_id || grade_id || "",
    plant_id: src?.plant_id || "",
    day: localISODate(from),
    time: fmtTime(from),
    comment: recipe?.comment || "",
  });
  const [items, setItems] = useState<{ material_id: string; qty: string }[]>(
    src ? src.items.map((i: any) => ({ material_id: i.material_id, qty: String(i.qty) })) : [{ material_id: "", qty: "" }],
  );
  const num = (x: string) => Number(x.replace(",", "."));
  const ok = v.grade_id && items.length && items.every((i) => i.material_id && num(i.qty) > 0);
  const save = async () => {
    const body = {
      grade_id: v.grade_id, plant_id: v.plant_id || null, valid_from: zonedISO(v.day, v.time), comment: v.comment || null,
      items: items.map((i) => ({ material_id: i.material_id, qty: num(i.qty) })),
    };
    try {
      if (recipe) await api.put(`/lab/recipes/${recipe.id}`, body);
      else await api.post("/lab/recipes", body);
      toast.ok("Черновик сохранён — проверьте и утвердите");
      onDone();
    } catch (e) { toast.error(e); }
  };
  // П-11 (22.10.2026): рецептура — в килограммах на 1 м³, вода — в литрах (склад остаётся в тоннах и кубометрах)
  const unit = (id: string) => ({ "т": "кг", "м³": "л" } as Record<string, string>)[mats.data?.items.find((m: any) => m.id === id)?.unit || ""] || mats.data?.items.find((m: any) => m.id === id)?.unit || "";
  return (
    <Modal title={recipe ? `Черновик: ${recipe.grade}, версия ${recipe.version}` : base ? `Новая версия: ${base.grade}` : "Новая рецептура"} onClose={onClose} actions={<>
      <Button onClick={onClose}>Отмена</Button>
      <Button variant="primary" disabled={!ok} onClick={save}>Сохранить черновик</Button>
    </>}>
      {!mats.data ? <Loading error={mats.error} /> : (
        <div className="stack tight">
          <div className="form-grid">
            <Field label="Марка"><Select value={v.grade_id} disabled={!!src} onChange={(e) => setV({ ...v, grade_id: e.target.value })} placeholder="Выберите" options={refs.grades.map((g) => ({ value: g.id, label: g.code }))} /></Field>
            <Field label="Завод" hint="Своя рецептура завода важнее общей"><Select value={v.plant_id} onChange={(e) => setV({ ...v, plant_id: e.target.value })} placeholder="Все заводы" options={refs.plants.map((p) => ({ value: p.id, label: p.name }))} /></Field>
            <Field label="Действует с"><Input type="date" value={v.day} onChange={(e) => setV({ ...v, day: e.target.value })} /></Field>
            <Field label="Время"><Input type="time" value={v.time} onChange={(e) => setV({ ...v, time: e.target.value })} /></Field>
          </div>
          <table className="table">
            <thead><tr><th>Материал</th><th className="num">На 1 м³ (кг, вода — л)</th><th /></tr></thead>
            <tbody>{items.map((it, i) => (
              <tr key={i}>
                <td><Select aria-label="Материал" value={it.material_id} placeholder="Выберите" onChange={(e) => setItems(items.map((x, j) => (j === i ? { ...x, material_id: e.target.value } : x)))}
                  options={mats.data.items.filter((m: any) => m.active || m.id === it.material_id).map((m: any) => ({ value: m.id, label: m.name }))} /></td>
                <td className="num"><div className="row tight" style={{ justifyContent: "flex-end", alignItems: "center", flexWrap: "nowrap" }}>
                  <Input inputMode="decimal" style={{ width: 100 }} aria-label="Норма на 1 м³" value={it.qty} onChange={(e) => setItems(items.map((x, j) => (j === i ? { ...x, qty: e.target.value } : x)))} />
                  <span className="small muted" style={{ width: 28, textAlign: "left" }}>{unit(it.material_id)}</span></div></td>
                <td className="right"><Button size="small" variant="ghost" onClick={() => setItems(items.filter((_, j) => j !== i))} aria-label="Убрать строку">✕</Button></td>
              </tr>
            ))}</tbody>
          </table>
          <div><Button size="small" variant="ghost" onClick={() => setItems([...items, { material_id: "", qty: "" }])}>+ материал</Button></div>
          <Field label="Комментарий" hint="Что изменилось и почему"><Input value={v.comment} onChange={(e) => setV({ ...v, comment: e.target.value })} /></Field>
          <p className="small muted" style={{ margin: 0 }}>Норма — в единице материала на 1 м³ бетона (цемент в тоннах: 0,35 т = 350 кг). Утверждённую рецептуру не меняют — создают новую версию; прошлые замесы остаются списанными по старой.</p>
        </div>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------- испытания

function Tests({ rows, edit, reload }: { rows: any[]; edit: boolean; reload: () => void }) {
  const { refs } = useCtx();
  const [grade, setGrade] = useState("");
  const [result, setResult] = useState("");
  const [dlg, setDlg] = useState<null | { test?: any }>(null);
  const list = rows.filter((t) => (!grade || t.grade_id === grade) && (!result || t.result === result));
  return (
    <div className="stack">
      <div className="row" style={{ alignItems: "flex-end" }}>
        <Field label="Марка"><Select value={grade} onChange={(e) => setGrade(e.target.value)} placeholder="Все" options={refs.grades.map((g) => ({ value: g.id, label: g.code }))} /></Field>
        <Field label="Результат"><Select value={result} onChange={(e) => setResult(e.target.value)} placeholder="Все" options={Object.entries(RESULT).map(([value, x]) => ({ value, label: x.label }))} /></Field>
        {edit && <Button variant="primary" onClick={() => setDlg({})}>Отбор проб</Button>}
      </div>
      {!list.length ? <p className="empty">Испытаний нет.</p> : (
        <div className="panel table-wrap">
          <table className="table">
            <thead><tr><th>Отбор</th><th>Завод</th><th>Марка</th><th>Заявка</th><th className="num">Возраст</th><th>Испытать</th><th className="num">Образцы, МПа</th><th className="num">Требуется / факт</th><th>Результат</th><th /></tr></thead>
            <tbody>{list.map((t) => (
              <tr key={t.id}>
                <td className="tabular">{fmtDateTime(t.sampled_at)}{t.mobility_cm != null && <div className="small muted">осадка конуса {fmtNum(t.mobility_cm, 0)} см</div>}</td>
                <td>{t.plant_name}</td><td>{t.grade}</td><td>{t.order_number ? `№${t.order_number}` : ""}</td>
                <td className="num">{t.age_days} сут</td>
                <td className={t.overdue ? "strong" : ""}>{fmtDate(t.due_at)}{t.overdue && <div className="small" style={{ color: "var(--warn)" }}>пора</div>}</td>
                <td className="num">{t.samples_mpa.length ? t.samples_mpa.map((x: number) => fmtNum(x, 1)).join(" · ") : "—"}</td>
                <td className="num">{t.required_mpa != null ? `${fmtNum(t.required_mpa, 1)} / ${fmtNum(t.actual_mpa, 1)}` : "—"}</td>
                <td><span className={`tag ${RESULT[t.result]?.cls}`}>{RESULT[t.result]?.label}</span>{t.age_days < 28 && t.result === "pending" && t.samples_mpa.length > 0 && <div className="small muted">промежуточное</div>}</td>
                <td className="right">{edit && <Button size="small" variant={t.samples_mpa.length ? "ghost" : "primary"} onClick={() => setDlg({ test: t })}>{t.samples_mpa.length ? "Изменить" : "Внести результаты"}</Button>}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
      <p className="small muted">Оценка — по ГОСТ 18105 (упрощённо): средняя прочность серии не ниже класса × коэффициент из настроек, и ни один образец не ниже класса. По 7 суткам вывод «не соответствует» не делается.</p>
      {dlg && <TestDialog test={dlg.test} onClose={() => setDlg(null)} onDone={() => { setDlg(null); reload(); }} />}
    </div>
  );
}

function TestDialog({ test, onClose, onDone }: { test?: any; onClose: () => void; onDone: () => void }) {
  const { refs, me } = useCtx();
  const toast = useToast();
  const plants = me.all_plants ? refs.plants : refs.plants.filter((p) => me.plant_ids.includes(p.id));
  const at = test ? toDate(test.sampled_at)! : new Date();
  const [v, setV] = useState({
    plant_id: test?.plant_id || plants[0]?.id || "",
    grade_id: test?.grade_id || "",
    day: localISODate(at), time: fmtTime(at),
    age_days: String(test?.age_days || 28),
    mobility_cm: test?.mobility_cm != null ? String(test.mobility_cm) : "",
    comment: test?.comment || "",
  });
  const [samples, setSamples] = useState<string[]>(test?.samples_mpa?.length ? test.samples_mpa.map(String) : test ? ["", "", ""] : []);
  const num = (x: string) => Number(x.replace(",", "."));
  const filled = samples.filter((x) => x.trim() !== "");
  const ok = v.plant_id && v.grade_id && num(v.age_days) >= 1 && filled.every((x) => num(x) > 0);
  const save = async () => {
    const body = {
      plant_id: v.plant_id, grade_id: v.grade_id, order_id: test?.order_id || null, trip_id: test?.trip_id || null,
      sampled_at: zonedISO(v.day, v.time), age_days: num(v.age_days),
      mobility_cm: v.mobility_cm ? num(v.mobility_cm) : null, samples_mpa: filled.map(num), comment: v.comment || null,
    };
    try {
      const r = test ? await api.put(`/lab/tests/${test.id}`, body) : await api.post("/lab/tests", body);
      toast.ok(r.result === "passed" ? "Соответствует классу" : r.result === "failed" ? "Не соответствует классу — нужна проверка" : "Сохранено");
      onDone();
    } catch (e) { toast.error(e); }
  };
  return (
    <Modal title={test ? `Испытание: ${test.grade}, отбор ${fmtDate(test.sampled_at)}` : "Отбор проб"} onClose={onClose} actions={<>
      <Button onClick={onClose}>Отмена</Button><Button variant="primary" disabled={!ok} onClick={save}>Сохранить</Button>
    </>}>
      <div className="form-grid">
        <Field label="Завод"><Select value={v.plant_id} onChange={(e) => setV({ ...v, plant_id: e.target.value })} options={plants.map((p) => ({ value: p.id, label: p.name }))} /></Field>
        <Field label="Марка"><Select value={v.grade_id} onChange={(e) => setV({ ...v, grade_id: e.target.value })} placeholder="Выберите" options={refs.grades.map((g) => ({ value: g.id, label: g.code }))} /></Field>
        <Field label="Дата отбора"><Input type="date" value={v.day} onChange={(e) => setV({ ...v, day: e.target.value })} /></Field>
        <Field label="Время"><Input type="time" value={v.time} onChange={(e) => setV({ ...v, time: e.target.value })} /></Field>
        <Field label="Возраст испытания, сут"><Select value={v.age_days} onChange={(e) => setV({ ...v, age_days: e.target.value })} options={["3", "7", "14", "28", "56"].map((x) => ({ value: x, label: x }))} /></Field>
        <Field label="Осадка конуса, см"><Input inputMode="decimal" value={v.mobility_cm} onChange={(e) => setV({ ...v, mobility_cm: e.target.value })} /></Field>
      </div>
      <div className="stack tight" style={{ marginTop: 8 }}>
        <b className="small">Прочность образцов, МПа</b>
        {samples.length === 0 ? <p className="small muted" style={{ margin: 0 }}>Результаты внесёте после испытания (через {v.age_days} сут).</p> : (
          <div className="row tight">{samples.map((x, i) => (
            <Input key={i} inputMode="decimal" style={{ width: 90 }} aria-label={`Образец ${i + 1}`} value={x} onChange={(e) => setSamples(samples.map((y, j) => (j === i ? e.target.value : y)))} />
          ))}</div>
        )}
        <div><Button size="small" variant="ghost" onClick={() => setSamples(samples.length ? [...samples, ""] : ["", "", ""])}>{samples.length ? "+ образец" : "Внести результаты сейчас"}</Button></div>
        <Field label="Комментарий"><Input value={v.comment} onChange={(e) => setV({ ...v, comment: e.target.value })} /></Field>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------- паспорта

function Passports() {
  const toast = useToast();
  const r = useAsync<any[]>(() => api.get("/lab/passports"), []);
  const [q, setQ] = useState("");
  if (!r.data) return <Loading error={r.error} onRetry={r.reload} />;
  const list = r.data.filter((p) => !q || `${p.number} ${p.order_number} ${p.customer}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="stack">
      <div className="row"><Input className="grow" placeholder="Номер паспорта, заявки или клиент" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Поиск паспорта" /></div>
      <p className="small muted" style={{ margin: 0 }}>Паспорт выдаётся из карточки заявки (кнопка «Выдать паспорт качества» после первой отгрузки).</p>
      {!list.length ? <p className="empty">Паспортов нет.</p> : (
        <div className="panel table-wrap">
          <table className="table">
            <thead><tr><th className="num">№</th><th>Выдан</th><th>Заявка</th><th>Клиент</th><th>Марка</th><th className="num">Объём</th><th>Выдал</th><th /></tr></thead>
            <tbody>{list.map((p) => (
              <tr key={p.id}>
                <td className="num strong">{p.number}</td><td className="tabular">{fmtDateTime(p.issued_at)}</td><td>№{p.order_number}</td><td>{p.customer}</td><td>{p.grade}</td>
                <td className="num">{qty(p.volume_m3 || 0, "м³")}</td><td className="small">{p.issued_by}</td>
                <td className="right"><Button size="small" onClick={() => api.open(`/lab/passports/${p.id}/print`).catch((e) => toast.error(e))}>Открыть</Button></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}
